import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    CapsuleButton,
    ContentSpace,
    MainPageWithButtons,
} from "../../Common/_components";
import { Icons } from "../../Everou/_assets";
import { genericActions } from "../../Everou/_actions/generic.actions";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { Organization } from "../../Organizations/_models/Organization";
import { OrganizationFormModal } from "./_components/OrganizationFormModal";
import { PMSOrganizationDetail } from "./PMSOrganizationDetail";
import { PMSActiveOrganizationStore } from "../_stores/PMSActiveOrganizationStore";

export function PMSOrganizationsModule() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const organizations: Organization[] = useSelector(
        Selectors.getOrganizations,
    );
    const selectedUid: string | null = useSelector(
        Selectors.getOrganizationSelectedUid,
    );
    const isRequesting: boolean = useSelector(
        Selectors.getOrganizationsIsRequesting,
    );

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    // The list request is dispatched after the first paint, so without this
    // flag the empty state would flash before the spinner on first mount.
    const [hasRequested, setHasRequested] = useState(false);

    useEffect(() => {
        dispatch(OrganizationsActions.listOrganizations());
        setHasRequested(true);
    }, [dispatch]);

    // With a single organization there is nothing to choose: go straight to
    // its detail. Only once per mount, so going back still reaches the list
    // (and the create button) instead of being bounced into the detail again.
    const hasAutoSelected = useRef(false);
    useEffect(() => {
        if (
            !selectedUid &&
            organizations.length === 1 &&
            !hasAutoSelected.current
        ) {
            hasAutoSelected.current = true;
            dispatch(
                OrganizationsActions.selectOrganization(organizations[0].uid),
            );
        }
    }, [dispatch, selectedUid, organizations]);

    const handleGoBack = useCallback(() => {
        dispatch(OrganizationsActions.selectOrganization(null));
        // The list is only fetched on mount; refresh it on the way back so
        // organizations created/removed meanwhile don't leave it stale.
        dispatch(OrganizationsActions.listOrganizations());
    }, [dispatch]);

    function handleSelect(uid: string) {
        dispatch(OrganizationsActions.selectOrganization(uid));
        // Opening an organization makes it the active one, so the header
        // shows its branding and Customization/Integrations operate on what
        // you are looking at instead of on whichever was picked before.
        dispatch(PMSActiveOrganizationStore.actionSet(uid));
    }

    async function handleCreate(name: string) {
        try {
            const created: Organization | undefined = (await dispatch(
                OrganizationsActions.createOrganization(name),
            )) as any;
            setIsCreateOpen(false);
            if (created?.uid) {
                dispatch(OrganizationsActions.selectOrganization(created.uid));
                dispatch(PMSActiveOrganizationStore.actionSet(created.uid));
            }
        } catch (error) {
            dispatch(genericActions.errorCatch(error));
        }
    }

    return <ContentSpace mainContent={mainContent()} />;

    function mainContent() {
        if (selectedUid) {
            return <PMSOrganizationDetail onGoBack={handleGoBack} />;
        }

        return (
            <>
                <MainPageWithButtons
                    size={"LARGE"}
                    pageTitle={t("mod_organizations_list_title")}
                    content={content()}
                    buttons={[
                        <CapsuleButton
                            text={t("mod_organizations_create")}
                            onClick={() => setIsCreateOpen(true)}
                        />,
                    ]}
                />
                <OrganizationFormModal
                    show={isCreateOpen}
                    title={t("mod_organizations_create")}
                    initialName={""}
                    onCancel={() => setIsCreateOpen(false)}
                    onSubmit={handleCreate}
                />
            </>
        );
    }

    function content() {
        if (!organizations.length) {
            if (isRequesting || !hasRequested) return spinner();
            return (
                <>
                    {intro()}
                    {empty()}
                </>
            );
        }
        return (
            <>
                {intro()}
                {list()}
            </>
        );
    }

    function intro() {
        return (
            <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-3">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "24px", height: "24px" }}
                />
                <span>
                    <Trans i18nKey={"mod_organizations_intro"}>
                        <strong>members placeholder</strong>
                        <strong>customization placeholder</strong>
                        <strong>integrations placeholder</strong>
                    </Trans>
                </span>
            </div>
        );
    }

    function list() {
        return (
            <div className="d-flex flex-column">
                {organizations.map((organization) => (
                    <div
                        key={organization.uid}
                        onClick={() => handleSelect(organization.uid)}
                        className="
                            d-flex
                            align-items-center
                            justify-content-between
                            border
                            rounded
                            p-2
                            mb-2
                            bg-white
                            cp
                            c-hover-240
                        "
                    >
                        <div className="font-weight-bold">
                            {organization.name}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    function empty() {
        return (
            <div className="d-flex align-items-center justify-content-center py-4">
                <h5>{t("mod_organizations_empty")}</h5>
            </div>
        );
    }

    function spinner() {
        return (
            <div className="w-100 pt-2 text-center">
                <div className="spinner-border c-text-primary" />
            </div>
        );
    }
}
